<script setup>
import ECommerceProductContentActionsCell from "@frontJs/components/tables/cells/projectManagement/seo/ECommerceProductContentActionsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import ExportButton from "@js/components/buttons/ExportButton.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import PriceCellV2 from "@js/components/tables/cells/PriceCellV2.vue";
import StatusCellV2 from "@js/components/tables/cells/StatusCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const {
  tenant,
  userSessionKey,
  withoutShortDescription,
  withoutDescription,
  withoutMetaTitle,
  withoutMetaDescription,
  standard,
  premium,
  hideNotDoneSeoApikomContent,
} = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
  userSessionKey: {
    type: String,
    required: false,
  },
  withoutShortDescription: {
    type: Boolean,
    required: false,
  },
  withoutDescription: {
    type: Boolean,
    required: false,
  },
  withoutMetaTitle: {
    type: Boolean,
    required: false,
  },
  withoutMetaDescription: {
    type: Boolean,
    required: false,
  },
  standard: {
    type: Boolean,
    required: false,
  },
  premium: {
    type: Boolean,
    required: false,
  },
  hideNotDoneSeoApikomContent: {
    type: Boolean,
    required: false,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
  updateData,
  selectionIds,
  exportData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "cmsProductId",
      header: () => t("label.cmsId"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.cmsProductId,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "sku",
      header: () => t("label.sku"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.sku,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "active",
      header: () => t("label.active"),
      cell: (props) =>
        h(StatusCellV2, {
          status: props.row.original.active,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.languageName,
          href: props.row.original.languageUrl,
          length: 140,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "finalPriceInclTax",
      header: () => t("label.price"),
      cell: (props) =>
        h(PriceCellV2, {
          price: props.row.original.finalPriceInclTax,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "realQuantity",
      header: () => t("label.qty"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.quantity,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "description",
      header: () => t("label.description"),
      cell: (props) =>
        h(StatusCellV2, {
          status: props.row.original.hasDescription,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "metaTitle",
      header: () => t("label.metaTitle"),
      cell: (props) =>
        h(StatusCellV2, {
          status: props.row.original.hasMetaTitle,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "metaDescription",
      header: () => t("label.metaDescription"),
      cell: (props) =>
        h(StatusCellV2, {
          status: props.row.original.hasMetaDescription,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "contentType",
      header: () => t("label.contentType"),
      cell: (props) =>
        props.row.original.seoContentProduct &&
        props.row.original.seoContentProduct.contentType
          ? h(PillCell, {
              pillValue: props.row.original.seoContentProduct.contentType.label,
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "descriptionUpdatedAt",
      header: () => t("label.descriptionUpdatedAt"),
      cell: (props) =>
        props.row.original.seoContentProduct &&
        props.row.original.seoContentProduct.descriptionUpdatedAt
          ? h(TextCellV2, {
              text: formatDate(
                props.row.original.seoContentProduct.descriptionUpdatedAt,
              ),
            })
          : h(CrossIconCell),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(ECommerceProductContentActionsCell, {
          row: innerProps.row,
          tenant,
          callback: updateData,
        }),
    }),
  ],
  url: route("front.projectManagement.seo.postECommerceProductContents", {
    tenant: tenant.id,
  }),
  exportUrl: route(
    "front.projectManagement.seo.postExportECommerceProductContents",
    {
      tenant: tenant.id,
    },
  ),
  userSessionKey,
  data: {
    withoutShortDescription,
    withoutDescription,
    withoutMetaTitle,
    withoutMetaDescription,
    standard,
    premium,
    hideNotDoneSeoApikomContent,
  },
  selectionIdKey: "cmsProductId",
  exportSelectionIdKey: "eCommerceProducts.cmsProductId",
  columnPinning: {
    right: ["actions"],
  },
  columnVisibility: {
    contentType: !(standard || premium),
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :filters="filters"
    :available-filters="availableFilters"
    :set-filters="setFilters"
    :set-global-filter="setGlobalFilter"
  >
    <template #actions>
      <ExportButton
        :csv-export-callback="() => exportData('csv')"
        :xlsx-export-callback="() => exportData('xlsx')"
        :disabled="selectionIds.length === 0"
      />
    </template>
  </DataTableLayout>
</template>
